import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { resolveSubscriptionStatus } from '../utils/subscriptionStatus';

function emptyStatus({ isLoading, status = null, error = null } = {}) {
  return {
    isLoading: Boolean(isLoading),
    isValid: false,
    hasPackage: false,
    status,
    error,
  };
}

export const usePackageValidation = (companyId, enabled = true, options = {}) => {
  const { blockWhileLoading = true } = options;
  const { companyData, loading: authLoading } = useAuth();

  return useMemo(() => {
    if (!enabled) {
      return emptyStatus({ isLoading: false });
    }

    if ((authLoading && !companyData) || (companyId && !companyData)) {
      return emptyStatus({ isLoading: blockWhileLoading });
    }

    const company = !companyId || companyData?.id === companyId ? companyData : null;
    if (!company) {
      return emptyStatus({
        isLoading: false,
        status: 'no_package',
        error: 'Company not found',
      });
    }

    const pkg = company.package;
    if (!pkg) {
      return emptyStatus({ isLoading: false, status: 'no_package' });
    }

    if (pkg.isAdminPackage || pkg.subscription?.isPermanent) {
      return {
        isLoading: false,
        isValid: true,
        hasPackage: true,
        status: 'active',
        error: null,
      };
    }

    const resolved = resolveSubscriptionStatus(
      pkg.subscription,
      pkg.subscription?.gracePeriodDays
    );

    return {
      isLoading: false,
      isValid: resolved.isValid,
      hasPackage: true,
      status: resolved.status,
      error: resolved.isValid ? null : 'No subscription found',
    };
  }, [authLoading, blockWhileLoading, companyData, companyId, enabled]);
};
